import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../lib/api";
import { useActiveCategories } from "../lib/meta";
import type { MerchantRule } from "../lib/types";
import { Badge, EmptyState, Panel, Spinner } from "./ui";

/**
 * What a statement's merchant text means.
 *
 * These are written by the import screen whenever a category is corrected and
 * "next time" is ticked, so this is where a wrong one gets fixed — otherwise a
 * single mis-taught rule would quietly mis-file every future statement.
 */
export function MerchantRulesTab({
  onError,
  onNotice,
}: {
  onError: (m: string) => void;
  onNotice: (m: string) => void;
}) {
  const categories = useActiveCategories();
  const [rules, setRules] = useState<MerchantRule[] | null>(null);
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState({
    matchOn: "MERCHANT" as MerchantRule["matchOn"],
    pattern: "",
    category: "",
    channel: "" as "" | "ONLINE" | "OFFLINE",
    isRegex: false,
  });

  const load = useCallback(async () => {
    try {
      setRules((await api.get<{ rules: MerchantRule[] }>("/statements/merchant-rules")).rules);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not load merchant rules");
    }
  }, [onError]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = filter.trim().toUpperCase();
    const list = rules ?? [];
    return q ? list.filter((r) => r.pattern.includes(q) || r.category.includes(q) || r.matchOn.includes(q)) : list;
  }, [rules, filter]);

  async function save(rule: MerchantRule, patch: Partial<MerchantRule>) {
    setRules((current) => current?.map((r) => (r.id === rule.id ? { ...r, ...patch } : r)) ?? null);
    try {
      await api.put(`/statements/merchant-rules/${rule.id}`, { ...rule, ...patch });
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not save that rule");
      void load();
    }
  }

  async function remove(rule: MerchantRule) {
    if (!confirm(`Forget "${rule.pattern}"? Statements will stop classifying it automatically.`)) return;
    try {
      await api.del(`/statements/merchant-rules/${rule.id}`);
      onNotice(`"${rule.pattern}" forgotten.`);
      void load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not delete that rule");
    }
  }

  async function add() {
    const category = draft.category || categories[0]?.key;
    if (!draft.pattern.trim() || !category) return;
    try {
      await api.post("/statements/merchant-rules", {
        matchOn: draft.matchOn,
        pattern: draft.pattern.trim(),
        isRegex: draft.isRegex,
        category,
        channel: draft.channel || null,
        priority: 50,
      });
      onNotice(`"${draft.pattern.trim().toUpperCase()}" added.`);
      setDraft({ matchOn: draft.matchOn, pattern: "", category: "", channel: "", isRegex: false });
      void load();
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Could not add that rule");
    }
  }

  if (rules === null) return <Spinner />;

  return (
    <Panel
      title="Merchant rules"
      subtitle="How a statement line becomes one of your categories. Higher priority wins, then the longer pattern — so AMAZON PAY beats AMAZON, and a category you corrected during review beats both."
      actions={
        <input
          className="input w-48 py-1 text-sm"
          placeholder="Search"
          aria-label="Search merchant rules"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      }
    >
      <div className="mb-5 grid gap-3 rounded-lg bg-ink-50 p-4 sm:grid-cols-[1.4fr_2fr_2fr_1fr_auto] sm:items-end">
        <div>
          <label className="label" htmlFor="mr-field">Read</label>
          <select
            id="mr-field"
            className="input"
            value={draft.matchOn}
            onChange={(e) => setDraft({ ...draft, matchOn: e.target.value as MerchantRule["matchOn"] })}
          >
            <option value="MERCHANT">The merchant name</option>
            <option value="STATEMENT_CATEGORY">The statement&apos;s own category</option>
          </select>
        </div>
        <div>
          <label className="label" htmlFor="mr-pattern">Containing</label>
          <input
            id="mr-pattern"
            className="input"
            placeholder={draft.matchOn === "MERCHANT" ? "SWIGGY" : "DINING"}
            value={draft.pattern}
            onChange={(e) => setDraft({ ...draft, pattern: e.target.value })}
          />
        </div>
        <div>
          <label className="label" htmlFor="mr-category">Becomes</label>
          <select
            id="mr-category"
            className="input"
            value={draft.category || categories[0]?.key || ""}
            onChange={(e) => setDraft({ ...draft, category: e.target.value })}
          >
            {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="mr-channel">Channel</label>
          <select
            id="mr-channel"
            className="input"
            value={draft.channel}
            onChange={(e) => setDraft({ ...draft, channel: e.target.value as "" | "ONLINE" | "OFFLINE" })}
          >
            <option value="">Leave alone</option>
            <option value="ONLINE">Online</option>
            <option value="OFFLINE">In store</option>
          </select>
        </div>
        <button className="btn-primary" onClick={() => void add()} disabled={!draft.pattern.trim()}>Add</button>
      </div>

      {shown.length === 0 ? (
        <EmptyState
          title={filter ? "Nothing matches that" : "No rules yet"}
          hint={filter ? undefined : "Import a statement and correct a category to teach the first one."}
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                <th className="py-2 pr-3">Reads</th>
                <th className="py-2 pr-3">Containing</th>
                <th className="py-2 pr-3">Category</th>
                <th className="py-2 pr-3">Channel</th>
                <th className="py-2 pr-3 text-right">Priority</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {shown.map((rule) => (
                <tr key={rule.id} className="border-b border-ink-100">
                  <td className="py-2 pr-3 whitespace-nowrap text-xs text-ink-500">
                    {rule.matchOn === "STATEMENT_CATEGORY" ? "Statement category" : "Merchant name"}
                  </td>
                  <td className="min-w-0 py-2 pr-3">
                    <span className="block truncate font-medium" title={rule.pattern}>{rule.pattern}</span>
                    {rule.isRegex && <Badge tone="info">expression</Badge>}
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      className="input py-1 text-sm"
                      value={rule.category}
                      aria-label={`Category for ${rule.pattern}`}
                      onChange={(e) => void save(rule, { category: e.target.value })}
                    >
                      {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                  </td>
                  <td className="py-2 pr-3">
                    <select
                      className="input py-1 text-sm"
                      value={rule.channel ?? ""}
                      aria-label={`Channel for ${rule.pattern}`}
                      onChange={(e) => void save(rule, { channel: (e.target.value || null) as MerchantRule["channel"] })}
                    >
                      <option value="">Leave alone</option>
                      <option value="ONLINE">Online</option>
                      <option value="OFFLINE">In store</option>
                    </select>
                  </td>
                  <td className="py-2 pr-3 text-right tabular-nums text-ink-500">{rule.priority}</td>
                  <td className="py-2 text-right">
                    <button className="btn-ghost text-xs text-rose-700" onClick={() => void remove(rule)}>Forget</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
